/**
 * FABP Format v2.0 - Fabric Project Format
 * 
 * Улучшенный формат для сохранения 3D проектов с оптимизацией:
 * - ZIP-архив с разделением данных
 * - Оптимизация размера (WebP для текстур, сжатие)
 * - Версионирование и обратная совместимость
 * - Метаданные и организация
 */

import JSZip from 'jszip';
import type { ProjectData, LayerData } from '../types';

// Версия формата
export const FABP_FORMAT_VERSION = '2.0';
export const FABP_SCHEMA_VERSION = 1;

// Магические байты для определения формата (PK - ZIP signature)
const ZIP_SIGNATURE = new Uint8Array([0x50, 0x4B, 0x03, 0x04]);

/**
 * Манифест проекта (manifest.json внутри ZIP)
 */
export interface FabpManifest {
  // Версионирование
  version: string;           // "2.0"
  formatVersion: number;     // 2
  schemaVersion: number;     // 1
  
  // Временные метки
  createdAt: string;         // ISO timestamp
  updatedAt: string;
  
  // Метаданные проекта
  metadata: {
    name?: string;
    author?: string;
    description?: string;
    tags?: string[];
    appVersion?: string;     // Версия приложения
  };
  
  // Превью (маленькое изображение для быстрого просмотра)
  thumbnail?: string;        // Относительный путь к файлу превью
  
  // 3D Модель
  model: {
    name: string;
    file?: string;           // Относительный путь к файлу модели
    format?: string;         // glb, gltf
  };
  
  // Окружение и фон
  environment: string;
  background: string | { gradient?: { top: string; bottom: string } };
  
  // Камера
  camera: {
    fov: number;
  };
  
  // Освещение
  lights: any;               // LightSettings
  lightPositions?: {
    top?: { x: number; y: number; z: number };
    bottom?: { x: number; y: number; z: number };
  };
  globalReflectivity?: number;
  
  // Слои материалов
  layers: Record<string, FabpLayerManifest>;
  
  // Статистика работы
  totalWorkTime?: number;
  
  // Контрольные суммы для верификации
  checksums?: {
    model?: string;
    textures?: Record<string, string>;
  };
}

/**
 * Манифест слоя (ссылки на файлы вместо base64)
 */
export interface FabpLayerManifest {
  name: string;
  preset: string | null;
  params: any;               // MaterialParams
  type?: 'layer' | 'print';
  visible?: boolean;         // Видимость слоя (только для принтов)
  labelColor?: string;       // UI-цвет кружка перед названием (только для принтов)
  /** Для принтов: активные UV-группы, на которых отображается принт */
  uvGroupIds?: string[];
  /** Legacy (single group) */
  uvGroupId?: string;
  
  // Ссылки на файлы текстур
  textures?: {
    map?: string;            // "textures/layer_001_map.webp"
    color?: string;
    normal?: string;
    roughness?: string;
    ao?: string;
    metalness?: string;
    opacity?: string;
  };
  
  // Для принтов:
  canvasDataFile?: string;   // "prints/print_001_canvas.json" - JSON данные Fabric.js для редактирования
  canvasImageFile?: string;  // "prints/print_001.webp" - результирующее изображение для быстрого отображения
  
  // Deprecated (для обратной совместимости с ранними версиями v2)
  canvasFile?: string;       // Старый формат - только изображение
}

/**
 * Опции экспорта
 */
export interface FabpExportOptions {
  // Качество сжатия текстур (0-1)
  textureQuality?: number;
  
  // Формат текстур
  textureFormat?: 'webp' | 'png' | 'jpeg';
  
  // Включить превью
  includeThumbnail?: boolean;
  thumbnailSize?: number;
  
  // Метаданные
  projectName?: string;
  author?: string;
  description?: string;
  
  // Режим совместимости (сохранять также в старом формате)
  legacyCompatibility?: boolean;
}

/**
 * Результат импорта
 */
export interface FabpImportResult {
  // Версия загруженного формата
  formatVersion: 'v1' | 'v2';
  
  // Данные проекта в стандартном формате
  projectData: ProjectData;
  
  // Метаданные (только для v2)
  metadata?: FabpManifest['metadata'];
  
  // Превью (base64, только для v2)
  thumbnail?: string;
}

/**
 * Основной класс для работы с FABP форматом
 */
export class FabpFormat {
  
  /**
   * Определяет формат файла (v1 JSON или v2 ZIP)
   */
  static async detectFormat(data: ArrayBuffer | string): Promise<'v1' | 'v2'> {
    if (typeof data === 'string') {
      // Строка - это JSON (v1)
      return 'v1';
    }
    
    const bytes = new Uint8Array(data.slice(0, 4));
    
    // Проверяем ZIP сигнатуру
    if (bytes[0] === ZIP_SIGNATURE[0] && 
        bytes[1] === ZIP_SIGNATURE[1] && 
        bytes[2] === ZIP_SIGNATURE[2] && 
        bytes[3] === ZIP_SIGNATURE[3]) {
      return 'v2';
    }
    
    // Иначе это JSON (v1)
    return 'v1';
  }
  
  /**
   * Экспорт проекта в FABP v2 формат (ZIP)
   */
  static async export(
    projectData: ProjectData,
    options: FabpExportOptions = {}
  ): Promise<Blob> {
    const {
      textureQuality = 0.85,
      textureFormat = 'webp',
      includeThumbnail = true,
      thumbnailSize = 256,
      projectName = 'Untitled',
      author = '',
      description = '',
    } = options;
    
    const zip = new JSZip();
    const now = new Date().toISOString();
    
    // Создаем манифест
    const manifest: FabpManifest = {
      version: FABP_FORMAT_VERSION,
      formatVersion: 2,
      schemaVersion: FABP_SCHEMA_VERSION,
      createdAt: now,
      updatedAt: now,
      metadata: {
        name: projectName,
        author,
        description,
        appVersion: '1.0.0',
      },
      model: {
        name: projectData.model.name,
        format: 'glb',
      },
      environment: projectData.environment,
      background: projectData.background,
      camera: {
        fov: projectData.cameraFov || 45,
      },
      lights: projectData.lights,
      lightPositions: projectData.lightPositions,
      globalReflectivity: projectData.globalReflectivity,
      layers: {},
      totalWorkTime: projectData.totalWorkTime,
    };
    
    // Создаем папки
    const texturesFolder = zip.folder('textures');
    const printsFolder = zip.folder('prints');
    const modelFolder = zip.folder('model');
    
    // Сохраняем модель
    if (projectData.model.dataURL) {
      const modelData = await this.dataURLToBlob(projectData.model.dataURL);
      if (modelData && modelFolder) {
        const modelFileName = `model.glb`;
        modelFolder.file(modelFileName, modelData);
        manifest.model.file = `model/${modelFileName}`;
      }
    }
    
    // Сохраняем aoMap модели (если есть)
    if (projectData.model.aoMap && typeof projectData.model.aoMap === 'string') {
      const aoMapData = await this.dataURLToBlob(projectData.model.aoMap);
      if (aoMapData && modelFolder) {
        const aoMapFileName = `model_ao.png`;
        modelFolder.file(aoMapFileName, aoMapData);
        (manifest.model as any).aoMap = `model/${aoMapFileName}`;
      }
    }
    
    // Счетчики для уникальных имен файлов
    let layerIndex = 0;
    let printIndex = 0;
    
    // Обрабатываем слои
    for (const [layerId, layer] of Object.entries(projectData.layers)) {
      const layerManifest: FabpLayerManifest = {
        name: layer.name,
        preset: layer.preset,
        params: layer.params,
        type: layer.type,
        visible: layer.visible,  // Сохраняем видимость слоя
        labelColor: (layer as any).labelColor,
        uvGroupIds: (layer as any).uvGroupIds,
        uvGroupId: (layer as any).uvGroupId,
        textures: {},
      };
      
      // Для обычных слоёв с пресетом - НЕ сохраняем текстуры!
      // Текстуры загрузятся с сервера по ID пресета при загрузке проекта.
      // Сохраняем только params (значения ползунков).
      // Это значительно уменьшает размер файла.
      
      // Сохраняем текстуры только если:
      // 1. Это принт (пользовательская графика)
      // 2. Нет пресета (пользователь загрузил свои текстуры)
      const shouldSaveTextures = layer.type === 'print' || !layer.preset;
      
      if (shouldSaveTextures && layer.maps && layer.type !== 'print') {
        // Сохраняем текстуры только для слоёв БЕЗ пресета (кастомные текстуры)
        const mapTypes = ['map', 'color', 'normal', 'roughness', 'ao', 'metalness', 'opacity'] as const;
        
        for (const mapType of mapTypes) {
          const mapValue = (layer.maps as any)[mapType];
          if (mapValue && typeof mapValue === 'string' && mapValue.startsWith('data:')) {
            // Конвертируем base64 в файл
            const extension = this.getOptimalExtension(mapType, textureFormat);
            const fileName = `layer_${layerIndex.toString().padStart(3, '0')}_${mapType}.${extension}`;
            
            try {
              const blob = await this.convertTextureToOptimalFormat(
                mapValue, 
                mapType, 
                textureFormat, 
                textureQuality
              );
              
              if (blob && texturesFolder) {
                texturesFolder.file(fileName, blob);
                layerManifest.textures![mapType] = `textures/${fileName}`;
              }
            } catch (e) {
              console.warn(`Ошибка конвертации текстуры ${mapType}:`, e);
              // Fallback: сохраняем как есть
              const originalBlob = await this.dataURLToBlob(mapValue);
              if (originalBlob && texturesFolder) {
                texturesFolder.file(fileName, originalBlob);
                layerManifest.textures![mapType] = `textures/${fileName}`;
              }
            }
          }
        }
      }
      
      // Обрабатываем принты - сохраняем и JSON данные canvas, и результирующее изображение
      if (layer.type === 'print') {
        const printBaseName = `print_${printIndex.toString().padStart(3, '0')}`;
        
        try {
          // 1. Сохраняем canvasData как JSON для редактирования (если есть)
          if (layer.canvasData && printsFolder) {
            // canvasData - это объект Fabric.js с объектами canvas
            const canvasJson = typeof layer.canvasData === 'string' 
              ? layer.canvasData 
              : JSON.stringify(layer.canvasData);
            
            const canvasJsonFileName = `${printBaseName}_canvas.json`;
            printsFolder.file(canvasJsonFileName, canvasJson);
            layerManifest.canvasDataFile = `prints/${canvasJsonFileName}`;
          }
          
          // 2. Сохраняем результирующее изображение принта из maps.map
          // Принты сохраняются как PNG без прохода через canvas (canvas.drawImage вызывает premultiplied alpha
          // precision loss, которая делает полупрозрачные края резкими/пикселизированными)
          const mapValue = (layer.maps as any)?.map;
          if (mapValue && typeof mapValue === 'string' && mapValue.startsWith('data:') && printsFolder) {
            const imageFileName = `${printBaseName}.png`;
            let blob: Blob | null = null;
            
            if (mapValue.startsWith('data:image/png')) {
              // PNG — сохраняем байты напрямую без canvas-конвертации
              blob = await this.dataURLToBlob(mapValue);
            } else {
              // WebP/JPEG (старые сохранения) — конвертируем в PNG через canvas
              blob = await this.convertTextureToOptimalFormat(mapValue, 'color', 'png', 1.0);
            }
            
            if (blob) {
              printsFolder.file(imageFileName, blob);
              layerManifest.canvasImageFile = `prints/${imageFileName}`;
            }
          }
        } catch (e) {
          console.warn('Ошибка сохранения принта:', e);
        }
        
        printIndex++;
      }
      
      manifest.layers[layerId] = layerManifest;
      layerIndex++;
    }
    
    // Создаем превью
    if (includeThumbnail) {
      try {
        const thumbnail = await this.createThumbnail(thumbnailSize);
        if (thumbnail) {
          zip.file('thumbnail.webp', thumbnail);
          manifest.thumbnail = 'thumbnail.webp';
        }
      } catch (e) {
        console.warn('Не удалось создать превью:', e);
      }
    }
    
    // Сохраняем манифест
    zip.file('manifest.json', JSON.stringify(manifest, null, 2));
    
    // Генерируем ZIP
    const zipBlob = await zip.generateAsync({
      type: 'blob',
      compression: 'DEFLATE',
      compressionOptions: {
        level: 6, // Баланс между скоростью и сжатием
      },
    });
    
    return zipBlob;
  }
  
  /**
   * Импорт проекта из FABP (поддержка v1 и v2)
   */
  static async import(file: File): Promise<FabpImportResult> {
    const arrayBuffer = await file.arrayBuffer();
    const format = await this.detectFormat(arrayBuffer);
    
    if (format === 'v1') {
      // Старый JSON формат
      const text = new TextDecoder().decode(arrayBuffer);
      const projectData = JSON.parse(text) as ProjectData;
      
      return {
        formatVersion: 'v1',
        projectData,
      };
    }
    
    // Новый ZIP формат (v2)
    return await this.importV2(arrayBuffer);
  }
  
  /**
   * Импорт FABP v2 (ZIP)
   */
  private static async importV2(arrayBuffer: ArrayBuffer): Promise<FabpImportResult> {
    const zip = await JSZip.loadAsync(arrayBuffer);
    
    // Читаем манифест
    const manifestFile = zip.file('manifest.json');
    if (!manifestFile) {
      throw new Error('Некорректный FABP файл: отсутствует manifest.json');
    }
    
    const manifestText = await manifestFile.async('text');
    const manifest = JSON.parse(manifestText) as FabpManifest;
    
    // Конвертируем в ProjectData
    const projectData: ProjectData = {
      version: manifest.version,
      model: {
        name: manifest.model.name,
        path: manifest.model.file,
      },
      environment: manifest.environment,
      background: manifest.background as any,
      lights: manifest.lights,
      lightPositions: manifest.lightPositions,
      globalReflectivity: manifest.globalReflectivity,
      layers: {},
      totalWorkTime: manifest.totalWorkTime,
      cameraFov: manifest.camera?.fov,
    };
    
    // Загружаем модель
    if (manifest.model.file) {
      const modelFile = zip.file(manifest.model.file);
      if (modelFile) {
        const modelData = await modelFile.async('base64');
        const mimeType = manifest.model.format === 'gltf' 
          ? 'model/gltf+json' 
          : 'model/gltf-binary';
        projectData.model.dataURL = `data:${mimeType};base64,${modelData}`;
      }
    }
    
    // Загружаем aoMap модели (если есть)
    const aoMapPath = (manifest.model as any).aoMap;
    if (aoMapPath) {
      const aoMapFile = zip.file(aoMapPath);
      if (aoMapFile) {
        const aoMapData = await aoMapFile.async('base64');
        const mimeType = this.getMimeTypeFromPath(aoMapPath);
        projectData.model.aoMap = `data:${mimeType};base64,${aoMapData}`;
      }
    }
    
    // Загружаем слои и текстуры
    for (const [layerId, layerManifest] of Object.entries(manifest.layers)) {
      const layerData: LayerData = {
        name: layerManifest.name,
        preset: layerManifest.preset,
        params: layerManifest.params,
        type: layerManifest.type,
        visible: layerManifest.visible,  // Загружаем видимость слоя
        labelColor: (layerManifest as any).labelColor,
        uvGroupIds: (layerManifest as any).uvGroupIds,
        uvGroupId: (layerManifest as any).uvGroupId,
        maps: {},
      };
      
      // Загружаем текстуры
      if (layerManifest.textures) {
        for (const [mapType, filePath] of Object.entries(layerManifest.textures)) {
          if (filePath) {
            const textureFile = zip.file(filePath);
            if (textureFile) {
              const textureData = await textureFile.async('base64');
              const mimeType = this.getMimeTypeFromPath(filePath);
              (layerData.maps as any)[mapType] = `data:${mimeType};base64,${textureData}`;
            }
          }
        }
      }
      
      // Загружаем данные для принтов
      if (layerManifest.type === 'print') {
        // 1. Загружаем canvasData (JSON для редактирования)
        if (layerManifest.canvasDataFile) {
          const canvasDataFile = zip.file(layerManifest.canvasDataFile);
          if (canvasDataFile) {
            const canvasDataText = await canvasDataFile.async('text');
            try {
              // Парсим JSON - это полные данные Fabric.js для редактирования
              layerData.canvasData = JSON.parse(canvasDataText);
            } catch (e) {
              console.warn('Ошибка парсинга canvasData:', e);
              layerData.canvasData = canvasDataText;
            }
          }
        }
        
        // 2. Загружаем результирующее изображение принта в maps.map
        if (layerManifest.canvasImageFile) {
          const imageFile = zip.file(layerManifest.canvasImageFile);
          if (imageFile) {
            const imageData = await imageFile.async('base64');
            const mimeType = this.getMimeTypeFromPath(layerManifest.canvasImageFile);
            (layerData.maps as any).map = `data:${mimeType};base64,${imageData}`;
          }
        }
        
        // 3. Обратная совместимость: старый формат canvasFile (только изображение)
        if (!layerManifest.canvasDataFile && !layerManifest.canvasImageFile && layerManifest.canvasFile) {
          const canvasFile = zip.file(layerManifest.canvasFile);
          if (canvasFile) {
            const canvasData = await canvasFile.async('base64');
            const mimeType = this.getMimeTypeFromPath(layerManifest.canvasFile);
            // В старом формате это было изображение, помещаем в maps.map
            (layerData.maps as any).map = `data:${mimeType};base64,${canvasData}`;
          }
        }
      }
      
      projectData.layers[layerId] = layerData;
    }
    
    // Загружаем превью
    let thumbnail: string | undefined;
    if (manifest.thumbnail) {
      const thumbnailFile = zip.file(manifest.thumbnail);
      if (thumbnailFile) {
        const thumbnailData = await thumbnailFile.async('base64');
        thumbnail = `data:image/webp;base64,${thumbnailData}`;
      }
    }
    
    return {
      formatVersion: 'v2',
      projectData,
      metadata: manifest.metadata,
      thumbnail,
    };
  }
  
  /**
   * Конвертирует dataURL в Blob
   */
  private static async dataURLToBlob(dataURL: string): Promise<Blob | null> {
    try {
      const response = await fetch(dataURL);
      return await response.blob();
    } catch (e) {
      console.warn('Ошибка конвертации dataURL в Blob:', e);
      return null;
    }
  }
  
  /**
   * Конвертирует текстуру в оптимальный формат
   */
  private static async convertTextureToOptimalFormat(
    dataURL: string,
    mapType: string,
    targetFormat: 'webp' | 'png' | 'jpeg',
    quality: number
  ): Promise<Blob | null> {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;
        
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          resolve(null);
          return;
        }
        
        ctx.drawImage(img, 0, 0);
        
        // Определяем оптимальный формат для типа карты
        let format = targetFormat;
        let actualQuality = quality;
        
        // Для нормалей используем PNG (без потерь)
        if (mapType === 'normal') {
          format = 'png';
          actualQuality = 1;
        }
        
        const mimeType = format === 'webp' 
          ? 'image/webp' 
          : format === 'png' 
            ? 'image/png' 
            : 'image/jpeg';
        
        canvas.toBlob(
          (blob) => resolve(blob),
          mimeType,
          actualQuality
        );
      };
      
      img.onerror = () => resolve(null);
      img.src = dataURL;
    });
  }
  
  /**
   * Получает оптимальное расширение файла для типа карты
   */
  private static getOptimalExtension(
    mapType: string, 
    preferredFormat: 'webp' | 'png' | 'jpeg'
  ): string {
    // Для нормалей всегда используем PNG (без потерь)
    if (mapType === 'normal') {
      return 'png';
    }
    return preferredFormat === 'jpeg' ? 'jpg' : preferredFormat;
  }
  
  /**
   * Получает MIME тип из пути к файлу
   */
  private static getMimeTypeFromPath(filePath: string): string {
    const ext = filePath.split('.').pop()?.toLowerCase();
    switch (ext) {
      case 'webp': return 'image/webp';
      case 'png': return 'image/png';
      case 'jpg':
      case 'jpeg': return 'image/jpeg';
      case 'glb': return 'model/gltf-binary';
      case 'gltf': return 'model/gltf+json';
      default: return 'application/octet-stream';
    }
  }
  
  /**
   * Создает превью текущего рендера
   */
  private static async createThumbnail(size: number): Promise<Blob | null> {
    return new Promise((resolve) => {
      try {
        // Получаем canvas рендера
        const canvas = document.querySelector('canvas');
        if (!canvas) {
          resolve(null);
          return;
        }
        
        // Создаем уменьшенную копию
        const thumbCanvas = document.createElement('canvas');
        const aspect = canvas.width / canvas.height;
        
        if (aspect >= 1) {
          thumbCanvas.width = size;
          thumbCanvas.height = Math.round(size / aspect);
        } else {
          thumbCanvas.height = size;
          thumbCanvas.width = Math.round(size * aspect);
        }
        
        const ctx = thumbCanvas.getContext('2d');
        if (!ctx) {
          resolve(null);
          return;
        }
        
        ctx.drawImage(canvas, 0, 0, thumbCanvas.width, thumbCanvas.height);
        
        thumbCanvas.toBlob(
          (blob) => resolve(blob),
          'image/webp',
          0.8
        );
      } catch (e) {
        console.warn('Ошибка создания превью:', e);
        resolve(null);
      }
    });
  }
  
  /**
   * Получает информацию о FABP файле без полной загрузки
   */
  static async getInfo(file: File): Promise<{
    formatVersion: 'v1' | 'v2';
    metadata?: FabpManifest['metadata'];
    thumbnail?: string;
    fileSize: number;
  }> {
    const arrayBuffer = await file.arrayBuffer();
    const format = await this.detectFormat(arrayBuffer);
    
    if (format === 'v1') {
      return {
        formatVersion: 'v1',
        fileSize: file.size,
      };
    }
    
    // Для v2 читаем только манифест и превью
    const zip = await JSZip.loadAsync(arrayBuffer);
    
    const manifestFile = zip.file('manifest.json');
    if (!manifestFile) {
      return {
        formatVersion: 'v2',
        fileSize: file.size,
      };
    }
    
    const manifestText = await manifestFile.async('text');
    const manifest = JSON.parse(manifestText) as FabpManifest;
    
    let thumbnail: string | undefined;
    if (manifest.thumbnail) {
      const thumbnailFile = zip.file(manifest.thumbnail);
      if (thumbnailFile) {
        const thumbnailData = await thumbnailFile.async('base64');
        thumbnail = `data:image/webp;base64,${thumbnailData}`;
      }
    }
    
    return {
      formatVersion: 'v2',
      metadata: manifest.metadata,
      thumbnail,
      fileSize: file.size,
    };
  }
  
  /**
   * Мигрирует данные из старого формата в новый
   */
  static migrateV1ToV2(projectData: ProjectData): ProjectData {
    // Добавляем версию если отсутствует
    if (!projectData.version) {
      projectData.version = '1.0';
    }
    
    // Нормализуем фон
    if (typeof projectData.background === 'string' && 
        !projectData.background.includes('gradient') &&
        projectData.background !== 'transparent') {
      // Оставляем как есть - это цвет
    }
    
    return projectData;
  }
}

/**
 * Скачивание FABP v2 файла
 */
export async function downloadFabp(
  projectData: ProjectData,
  filename: string,
  options?: FabpExportOptions
): Promise<void> {
  const blob = await FabpFormat.export(projectData, options);
  
  const a = document.createElement('a');
  a.download = filename.endsWith('.fabp') ? filename : `${filename}.fabp`;
  a.href = URL.createObjectURL(blob);
  a.click();
  
  // Освобождаем память
  setTimeout(() => URL.revokeObjectURL(a.href), 1000);
}

/**
 * Форматирование размера файла
 */
export function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

